// src/pages/Transactions/TransactionCard.jsx
import React from 'react';
import { PLATFORM } from '../../constants';
import { transactionStyles, getStatusColor } from './styles';

// SVG Icons
const ShoppingBagIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);

const DollarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const XCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

const getStatusIcon = (status) => {
  switch(status) {
    case 'completed':
      return <CheckCircleIcon />;
    case 'pending':
    case 'processing':
      return <ClockIcon />;
    case 'failed':
    case 'cancelled':
      return <XCircleIcon />;
    default:
      return <ClockIcon />;
  }
};

const TransactionCard = ({ transaction, formatDate }) => {
  return (
    <div style={transactionStyles.transactionCard}>
      <div style={transactionStyles.transactionHeader}>
        <div style={transactionStyles.transactionType}>
          <div style={transactionStyles.typeIconWrapper}>
            {transaction.type === 'purchase' ? <ShoppingBagIcon /> : <DollarIcon />}
          </div>
          <span style={transactionStyles.typeText}>
            {transaction.type === 'purchase' ? 'Purchase' : 'Sale'}
          </span>
        </div>
        <div 
          style={{
            ...transactionStyles.statusBadge,
            backgroundColor: getStatusColor(transaction.status)
          }}
        >
          {getStatusIcon(transaction.status)}
          <span style={transactionStyles.statusText}>
            {transaction.status || 'Completed'}
          </span>
        </div>
      </div>

      <div style={transactionStyles.transactionDetails}>
        <div style={transactionStyles.itemInfo}>
          <h3 style={transactionStyles.itemTitle}>
            {transaction.listingTitle || 'Unknown Item'}
            {transaction.purchaseQuantity && transaction.purchaseQuantity > 1 && (
              <span style={{ fontSize: '0.875rem', color: '#666', marginLeft: '8px' }}>
                ×{transaction.purchaseQuantity}
              </span>
            )}
          </h3>
          <div style={transactionStyles.otherParty}>
            {transaction.type === 'purchase' ? 'Seller: ' : 'Buyer: '}
            <span style={transactionStyles.partyName}>
              {transaction.otherUser?.name || 'Unknown User'}
            </span>
          </div>
        </div>

        <div style={transactionStyles.amountInfo}>
          <div style={transactionStyles.totalAmount}>
            {transaction.type === 'sale' 
              ? `$${transaction.sellerReceives?.toFixed(2) || transaction.totalAmount?.toFixed(2) || '0.00'}`
              : `$${transaction.totalAmount?.toFixed(2) || '0.00'}`
            }
          </div>
          {transaction.type === 'sale' && (transaction.platformFee > 0 || transaction.serviceFee > 0) && (
            <div style={transactionStyles.amountBreakdown}>
              <div style={transactionStyles.breakdownItem}>
                Sale Price: ${transaction.originalPrice?.toFixed(2) || ((transaction.totalAmount || 0) + (transaction.serviceFee || 0)).toFixed(2)}
              </div>
              <div style={transactionStyles.commissionFee}>
                Platform Fee ({(PLATFORM.FEE_PERCENTAGE * 100)}%): -${transaction.platformFee?.toFixed(2) || transaction.serviceFee?.toFixed(2) || '0.00'}
              </div>
              <div style={transactionStyles.commissionFee}>
                Stripe Fee (2.9% + $0.30): -${transaction.stripeFee?.toFixed(2) || '0.00'}
              </div>
              <div style={{...transactionStyles.breakdownItem, fontWeight: '600', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e5e7eb'}}>
                You Receive: ${transaction.sellerReceives?.toFixed(2) || transaction.totalAmount?.toFixed(2) || '0.00'}
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={transactionStyles.transactionFooter}>
        <div style={transactionStyles.transactionDate}>
          <CalendarIcon />
          <span>{formatDate(transaction.createdAt)}</span>
        </div>
        <div style={transactionStyles.transactionId}>
          ID: {transaction.id.slice(0, 8)}
        </div>
      </div>
    </div>
  );
};

export default TransactionCard;